import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { ChevronDown } from "lucide-react";
import { LogoMark } from "../components/Logo";

const EASE = [0.22, 1, 0.36, 1];
const BASE_DELAY = 1.25;

function IntroOverlay({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 1250);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-[#050505] flex flex-col items-center justify-center gap-7"
      exit={{ y: "-100%" }}
      transition={{ duration: 0.75, ease: [0.76, 0, 0.24, 1] }}
      data-testid="intro-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.82 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
      >
        <LogoMark className="w-44 sm:w-60 h-auto" />
      </motion.div>
      <motion.div
        className="h-px bg-electric"
        initial={{ width: 0 }}
        animate={{ width: 190 }}
        transition={{ duration: 0.8, delay: 0.3, ease: "easeInOut" }}
      />
      <motion.p
        className="text-[10px] tracking-[0.5em] uppercase text-gray-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55, duration: 0.4 }}
      >
        ECU Remapping · Car Tuning · Diagnostics
      </motion.p>
    </motion.div>
  );
}

export default function Hero({ content }) {
  const hero = content.hero;
  const ref = useRef(null);
  const lenis = useLenis();
  const [introDone, setIntroDone] = useState(false);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (hash) => (e) => {
    e.preventDefault();
    lenis?.scrollTo(hash, { offset: -64, duration: 1.4 });
  };

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden flex items-end" data-testid="hero-section">
      <AnimatePresence>{!introDone && <IntroOverlay onDone={() => setIntroDone(true)} />}</AnimatePresence>

      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        {hero.image && (
          <img
            src={hero.image}
            alt="Performance car in dark studio lighting"
            className="h-full w-full object-cover object-center"
          />
        )}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/25" />
      <div className="absolute inset-0 bg-[radial-gradient(65%_55%_at_72%_38%,rgba(6,69,243,0.16),transparent_70%)]" />

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-24 pt-40">
        <motion.p
          className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.4em] text-gray-400 mb-6"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: BASE_DELAY, ease: EASE }}
        >
          Rochdale · Greater Manchester
        </motion.p>

        <h1 className="font-display font-bold uppercase tracking-tighter leading-[0.92] text-5xl sm:text-7xl lg:text-8xl" data-testid="hero-headline">
          {hero.headline.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "text-outline" : ""} ${i === hero.headline.length - 1 ? "text-electric-bright" : "text-white"}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: BASE_DELAY + 0.12 + i * 0.13, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-7 max-w-xl text-sm sm:text-base text-gray-400 leading-relaxed"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: BASE_DELAY + 0.55, ease: EASE }}
          data-testid="hero-subtext"
        >
          {hero.subtext}
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: BASE_DELAY + 0.7, ease: EASE }}
        >
          <a
            href="#booking"
            onClick={go("#booking")}
            data-testid="hero-book-button"
            className="bg-electric hover:bg-electric-bright text-white text-sm font-bold uppercase tracking-[0.18em] px-9 py-4 transition-colors shadow-[0_0_28px_rgba(6,69,243,0.4)]"
          >
            Book a Service
          </a>
          <a
            href="#services"
            onClick={go("#services")}
            data-testid="hero-explore-button"
            className="border border-white/25 hover:border-white text-white text-sm font-bold uppercase tracking-[0.18em] px-9 py-4 transition-colors"
          >
            Explore Our Services
          </a>
        </motion.div>

        <motion.div
          className="mt-16 flex items-center justify-between border-t border-white/10 pt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: BASE_DELAY + 0.9 }}
        >
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gray-500">
            ECU Remapping — Stage 1 Tuning — Vehicle Diagnostics
          </p>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="text-gray-500"
          >
            <ChevronDown size={18} />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
